import useScrollAnimation from '@/hooks/useScrollAnimation';
import { books2026 } from '@/mocks/books2026';
import { getBookPublicationBadge, getBookPublicationBadgeClass, getBookPublicationBadgeLabel } from '../../../../../shared/book-publication-status';

export default function Books2026Section() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex flex-col items-center justify-center bg-background-100 overflow-hidden py-20 md:py-0"
    >
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, oklch(0.48 0.085 260) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      <div
        className={`relative z-10 flex flex-col items-center text-center px-6 md:px-10 w-full max-w-[1200px] transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <span className="inline-block text-xs text-foreground-400 uppercase tracking-widest mb-4">
          最新發行
        </span>

        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
          2026 最新發行
        </h2>

        <p className="text-foreground-500 text-sm md:text-base leading-relaxed max-w-[640px] mb-12 text-balance">
          探索沐燁文化於 2026 年最新發行的彩色知識與科普書籍。
        </p>

        {/* Book Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5 w-full">
          {books2026.map((book) => (
            <div
              key={book.id}
              className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300/60 transition-all duration-300 text-left flex flex-col"
            >
              {/* Cover */}
              <div className="relative aspect-[3/4] overflow-hidden bg-background-200/50 shrink-0">
                <img
                  src={book.coverUrl}
                  alt={book.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {(() => {
                  const badge = getBookPublicationBadge('publishDate' in book ? book.publishDate : '');
                  return badge ? (
                    <span className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-xs font-medium ${getBookPublicationBadgeClass(badge)}`}>
                      {getBookPublicationBadgeLabel(badge)}
                    </span>
                  ) : null;
                })()}
              </div>

              {/* Info */}
              <div className="p-3 md:p-4 flex flex-col flex-1">
                <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 leading-snug mb-1.5 line-clamp-3">
                  {book.title}
                </h3>

                <p className="text-xs md:text-sm text-foreground-600 mb-1">
                  {book.author}
                </p>

                <p className="text-[11px] text-foreground-400 font-mono mb-2">
                  ISBN {book.isbn}
                </p>

                <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                  {book.description}
                </p>

                {/* Spacer to push button to bottom */}
                <div className="flex-1" />

                <a
                  href={book.searchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 w-full px-3 py-2 bg-primary-50 text-primary-700 text-xs font-medium rounded-md hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看書籍資訊
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-external-link-line"></i>
                  </span>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom: Catalog disclaimer + action buttons */}
        <div className="mt-12 flex flex-col items-center gap-4 w-full max-w-[720px]">
          <p className="text-foreground-500 text-xs md:text-sm leading-relaxed text-center text-balance">
            完整書名、作者、內容來源及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-database-2-line"></i>
              </span>
              前往書籍資料庫
            </a>

            <a
              href="https://www.books.com.tw/web/sys_puballb/books/?pubid=sungpo7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-foreground-300 text-foreground-700 text-sm font-medium rounded-md hover:border-foreground-400 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shopping-bag-line"></i>
              </span>
              博客來品牌專區
            </a>

            <a
              href="https://sonbook.net/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-foreground-300 text-foreground-700 text-sm font-medium rounded-md hover:border-foreground-400 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-global-line"></i>
              </span>
              崧博出版官網
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
